import React from 'react';
import './App.css';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { PrivateRoute, AdminRoute } from './components/PrivateRoute';
import Header from './components/Header';
import Hero from './components/Hero';
import Features from './components/Features';
import Reviews from './components/Reviews';
import Footer from './components/Footer';
import Pricing from './pages/Pricing';
import FAQ from './pages/FAQ';
import FindCalendar from './pages/FindCalendar';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import GetStarted from './pages/GetStarted';
import ManageServices from './pages/ManageServices';
import ManageUsers from './pages/ManageUsers';
import ManageCustomers from './pages/ManageCustomers';  // Calendar-scoped customer management (Jan 2026)
import ManageCalendars from './pages/ManageCalendars';
import ManageArchives from './pages/ManageArchives';
import ManageServers from './pages/ManageServers';
import ManagePlans from './pages/ManagePlans';
import EditPlan from './pages/EditPlan';
import ManageExternalServices from './pages/ManageExternalServices';
import EmailSettings from './pages/EmailSettings';
import ServerCalendar from './pages/ServerCalendar';
import CalendarView from './pages/CalendarView';
import EditUser from './pages/EditUser';
import DeleteUser from './pages/DeleteUser';
import DeleteService from './pages/DeleteService';
import ProfileSettings from './pages/ProfileSettings';
import ManageAccount from './pages/ManageAccount';
import CalendarAdminDashboard from './pages/CalendarAdminDashboard';
import EditCalendar from './pages/EditCalendar';
import CustomerLogin from './pages/CustomerLogin';
import CustomerRegister from './pages/CustomerRegister';
import CustomerStatistics from './pages/CustomerStatistics';
import CustomerBookAppointment from './pages/CustomerBookAppointment';
import ManageNotificationTemplates from './pages/ManageNotificationTemplates';
import ManageReminders from './pages/ManageReminders';
import CalendarRedirect from './pages/CalendarRedirect';
import VerifyEmail from './pages/VerifyEmail';
import VerificationPending from './pages/VerificationPending';
import ResendVerification from './pages/ResendVerification';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentCancel from './pages/PaymentCancel';
import Marketing from './pages/Marketing';
import MarketingTemplates from './pages/MarketingTemplates';
import MarketingCampaigns from './pages/MarketingCampaigns';
import MarketingConsole from './pages/MarketingConsole';
import BillingPortal from './pages/BillingPortal';
import Support from './pages/Support';
import ManageFAQ from './pages/ManageFAQ';
import ManageLogging from './pages/ManageLogging';
import UserGuidePage from './pages/UserGuidePage';

const Home = () => {
  return (
    <div className="min-h-screen">
      <Header />
      <Hero />
      <Features />
      <Reviews />
      <Footer />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/find-calendar" element={<FindCalendar />} />
            <Route path="/support" element={<Support />} />
            <Route path="/customer/login/:symbolicName" element={<CustomerLogin />} />
            <Route path="/customer/register/:symbolicName" element={<CustomerRegister />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/verification-pending" element={<VerificationPending />} />
            <Route path="/resend-verification" element={<ResendVerification />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />
            <Route path="/get-started" element={<GetStarted />} />
            <Route path="/dashboard" element={
              <PrivateRoute>
                <Dashboard />
              </PrivateRoute>
            } />
            <Route path="/dashboard/statistics" element={
              <AdminRoute>
                <CalendarAdminDashboard />
              </AdminRoute>
            } />
            <Route path="/customer/statistics" element={
              <PrivateRoute>
                <CustomerStatistics />
              </PrivateRoute>
            } />
            <Route path="/customer/book-appointment" element={
              <PrivateRoute>
                <CustomerBookAppointment />
              </PrivateRoute>
            } />
            <Route path="/profile-settings" element={
              <PrivateRoute>
                <ProfileSettings />
              </PrivateRoute>
            } />
            <Route path="/user-guide" element={
              <AdminRoute>
                <UserGuidePage />
              </AdminRoute>
            } />
            <Route path="/manage-account" element={
              <AdminRoute>
                <ManageAccount />
              </AdminRoute>
            } />
            <Route path="/billing" element={
              <AdminRoute>
                <BillingPortal />
              </AdminRoute>
            } />
            <Route path="/admin/services" element={
              <AdminRoute>
                <ManageServices />
              </AdminRoute>
            } />
            <Route path="/admin/services/delete/:serviceId" element={
              <AdminRoute>
                <DeleteService />
              </AdminRoute>
            } />
            <Route path="/admin/calendars" element={
              <AdminRoute>
                <ManageCalendars />
              </AdminRoute>
            } />
            <Route path="/admin/archives" element={
              <AdminRoute>
                <ManageArchives />
              </AdminRoute>
            } />
            <Route path="/admin/plans" element={
              <AdminRoute>
                <ManagePlans />
              </AdminRoute>
            } />
            <Route path="/admin/plans/new" element={
              <AdminRoute>
                <EditPlan />
              </AdminRoute>
            } />
            <Route path="/admin/plans/edit/:planId" element={
              <AdminRoute>
                <EditPlan />
              </AdminRoute>
            } />
            <Route path="/admin/external-services" element={
              <AdminRoute>
                <ManageExternalServices />
              </AdminRoute>
            } />
            <Route path="/admin/notification-templates" element={
              <AdminRoute>
                <ManageNotificationTemplates />
              </AdminRoute>
            } />
            <Route path="/admin/faq" element={
              <AdminRoute>
                <ManageFAQ />
              </AdminRoute>
            } />
            <Route path="/admin/reminders" element={
              <AdminRoute>
                <ManageReminders />
              </AdminRoute>
            } />
            <Route path="/email-settings" element={
              <AdminRoute>
                <EmailSettings />
              </AdminRoute>
            } />
            <Route path="/marketing" element={
              <AdminRoute>
                <Marketing />
              </AdminRoute>
            } />
            <Route path="/marketing/templates" element={
              <AdminRoute>
                <MarketingTemplates />
              </AdminRoute>
            } />
            <Route path="/marketing/campaigns" element={
              <AdminRoute>
                <MarketingCampaigns />
              </AdminRoute>
            } />
            <Route path="/marketing/console" element={
              <AdminRoute>
                <MarketingConsole />
              </AdminRoute>
            } />
            <Route path="/admin/servers" element={
              <AdminRoute>
                <ManageServers />
              </AdminRoute>
            } />
            <Route path="/admin/servers/:serverId/calendar" element={
              <AdminRoute>
                <ServerCalendar />
              </AdminRoute>
            } />
            <Route path="/admin/calendars/edit/:calendarId" element={
              <AdminRoute>
                <EditCalendar />
              </AdminRoute>
            } />
            <Route path="/calendar/:calendarId" element={
              <PrivateRoute>
                <CalendarView />
              </PrivateRoute>
            } />
            <Route path="/admin/users" element={
              <AdminRoute>
                <ManageUsers />
              </AdminRoute>
            } />
            {/* Calendar-scoped customer management (Jan 2026) */}
            <Route path="/admin/customers" element={
              <AdminRoute>
                <ManageCustomers />
              </AdminRoute>
            } />
            <Route path="/admin/users/edit/:userId" element={
              <AdminRoute>
                <EditUser />
              </AdminRoute>
            } />
            <Route path="/admin/users/delete/:userId" element={
              <AdminRoute>
                <DeleteUser />
              </AdminRoute>
            } />
            {/* Super Admin only - Logging Management */}
            <Route path="/admin/logging" element={
              <AdminRoute>
                <ManageLogging />
              </AdminRoute>
            } />
            {/* Short URL for calendars - must be last to not conflict with other routes */}
            <Route path="/user/:symbolicName" element={<CalendarRedirect />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;